import { useState, useEffect } from 'react';
import {
	Box,
	Button,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	CircularProgress,
	IconButton,
	Autocomplete,
	TextField,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { authedApiFetch } from '../../utils/Api';
import { useRevalidator } from 'react-router';

const MappingReview = ({
	fileName,
	fileId,
	tenantId,
	showNotification,
	onClose,
	approved,
}) => {
	// React Router hooks
	// For imperatively reloading the page data after completing actions
	const revalidator = useRevalidator();

	// State management
	const [rawPreviewData, setRawPreviewData] = useState(null);
	const [previewColumns, setPreviewColumns] = useState([]);
	const [previewRows, setPreviewRows] = useState([]);
	const [sourceHeaders, setSourceHeaders] = useState([]);
	const [availableSourceHeaders, setAvailableSourceHeaders] = useState([]);
	const [allTargetFields, setAllTargetFields] = useState([]);
	const [availableTargetFields, setAvailableTargetFields] = useState([]);
	const [mappings, setMappings] = useState([]);
	const [originalMappings, setOriginalMappings] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Load data and set state when file ID or tenant ID changes
	useEffect(() => {
		async function loadMappingAndPreview() {
			setLoading(true);
			try {
				const mappingPromise = authedApiFetch({
					endpoint: `/tenants/${tenantId}/table_mapping_source_files/${fileId}/table_mapping`,
					method: 'GET',
				});

				const previewPromise = authedApiFetch({
					endpoint: `/tenants/${tenantId}/table_mapping_source_files/${fileId}/show_preview`,
					method: 'GET',
				});

				const [mappingResponse, previewResponse] = await Promise.all([
					mappingPromise,
					previewPromise,
				]);

				// Get all target fields and their names from target schema
				const schemaProperties =
					mappingResponse.target_schema.schema.properties;
				const targetFields = Object.values(schemaProperties);
				const targetFieldNames = targetFields.map((field) => field.api_name);

				// convert BE format into structure used by this component.
				// TODO: refactor the BE so that it is generated & stored in this format
				const formattedMappings = [];
				const originalMappings = mappingResponse.mapping;
				// # destructure key/value pair; key is source column, value is object
				Object.entries(originalMappings).forEach(
					([sourceColumn, mappingInfo]) => {
						// skip if mapping info is null for a given source column.
						if (!mappingInfo) return;

						// convert key/value pair into flat object with required fields
						formattedMappings.push({
							targetField: mappingInfo.target_field,
							sourceHeader: sourceColumn,
							confidence: parseFloat(mappingInfo.confidence),
						});
					}
				);

				// Set state with fetched data
				setAllTargetFields(targetFieldNames || []);
				setSourceHeaders(mappingResponse.source_columns || []);
				setMappings(formattedMappings);
				setOriginalMappings(originalMappings);
				setRawPreviewData(previewResponse?.rows);
			} catch (err) {
				console.error('Error loading mapping data:', err);
				setError('Failed to load mapping data. Please try again later.');
			} finally {
				setLoading(false);
			}
		}

		if (fileId && tenantId) {
			loadMappingAndPreview();
		}
	}, [fileId, tenantId]);

	// update currently available values when mappings change
	useEffect(() => {
		if (mappings && mappings.length > 0) {
			if (allTargetFields && allTargetFields.length > 0) {
				// Use set intersection to find currently unmapped target fields
				const mappedTargetFields = new Set(
					mappings.map((mapping) => mapping.targetField)
				);
				const allTargetFieldsSet = new Set(allTargetFields);
				const availableTargetFieldsSet =
					allTargetFieldsSet.difference(mappedTargetFields);
				const newAvailableTargetFields = Array.from(availableTargetFieldsSet);

				setAvailableTargetFields(newAvailableTargetFields);
			}

			if (sourceHeaders && sourceHeaders.length > 0) {
				// Use set intersection to find currently unmapped target fields
				const mappedSourceHeaders = new Set(
					mappings.map((mapping) => mapping.sourceHeader)
				);
				const allSourceHeadersSet = new Set(sourceHeaders);
				const availableSourceHeadersSet =
					allSourceHeadersSet.difference(mappedSourceHeaders);
				const newAvailableSourceHeaders = Array.from(availableSourceHeadersSet);

				setAvailableSourceHeaders(newAvailableSourceHeaders);
			}
		}
	}, [allTargetFields, sourceHeaders, mappings]);

	// (re-)process preview data when raw data and mappings are loaded or changed
	useEffect(() => {
		if (rawPreviewData && mappings) {
			if (!rawPreviewData.length === 0) {
				setPreviewColumns([]);
				setPreviewRows([]);
				return;
			}

			// Create columns for the preview - only show target fields that have a mapping
			const columns = mappings
				.filter((mapping) => mapping.sourceHeader && mapping.targetField)
				.map((mapping) => ({
					field: mapping.targetField,
					headerName: mapping.targetField,
					width: 180,
					renderHeader: (params) => (
						<Box>
							<Typography variant='subtitle2'>
								{params.colDef?.headerName}
							</Typography>
							<Typography variant='caption' color='textSecondary'>
								Source: {mapping.sourceHeader}
							</Typography>
						</Box>
					),
				}));

			setPreviewColumns(columns);

			// Create rows from preview data
			const rows = rawPreviewData.map((row, index) => {
				const transformedRow = { id: index };

				// For each target field with valid mapping, get value from source column
				mappings.forEach((mapping) => {
					if (mapping.sourceHeader && mapping.targetField) {
						transformedRow[mapping.targetField] = row[mapping.sourceHeader];
					}
				});

				return transformedRow;
			});

			setPreviewRows(rows);
		}
	}, [rawPreviewData, mappings]);

	// shared logic for formatting and sending table mapping update API request
	const updateMappingRecord = async () => {
		// convert back into format expected by BE (see earlier TODO: refactor)
		const updatedMapping = {};
		// reverse the keys and add dropped values
		mappings.forEach((mapping) => {
			// ignore incomplete mappings
			// TODO: show error and prevent saving if they are present, instead
			if (!mapping.sourceHeader || !mapping.targetField) return;

			updatedMapping[mapping.sourceHeader] = {
				confidence: mapping.confidence,
				target_field: mapping.targetField,
				source_column: mapping.sourceHeader,
			};
		});
		// add back in blank entries for unmapped or skipped source fields
		sourceHeaders.forEach((sourceHeader) => {
			if (!updatedMapping[sourceHeader]) {
				updatedMapping[sourceHeader] = null;
			}
		});

		await authedApiFetch({
			endpoint: `/tenants/${tenantId}/table_mapping_source_files/${fileId}/table_mapping`,
			method: 'PATCH',
			payload: { table_mapping: { mapping: updatedMapping } },
		});
	};

	const handleSaveMapping = async () => {
		try {
			await updateMappingRecord();

			// Imperatively revalidate b/c not using React Router action.
			// Refreshes all data on page and parent pages, to get updated records.
			// TODO: refactor so that this goes through a clientAction.
			revalidator.revalidate();

			showNotification('Mapping template saved successfully', 'success');
		} catch (error) {
			showNotification('Error saving mapping template', 'error');
			console.error('Error:', error);
		}
	};

	const handleApproveMapping = async () => {
		try {
			// save table mapping
			await updateMappingRecord();

			// set table_mapping_source_files status to "mapping_approved"
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/table_mapping_source_files/${fileId}`,
				method: 'PATCH',
				payload: { table_mapping_source_file: { status: 'mapping_approved' } },
			});

			// Imperatively revalidate b/c not using React Router action.
			// Refreshes all data on page and parent pages, to get updated records.
			// TODO: refactor so that this goes through a clientAction.
			revalidator.revalidate();

			showNotification('Saved and approved mapping', 'success');
			onClose();
		} catch (error) {
			showNotification('Failed to save and approve mapping', 'error');
			console.error('Error:', error);
		}
	};

	const getConfidenceColor = (confidence) => {
		if (confidence >= 0.9) return 'success';
		if (confidence >= 0.7) return 'warning';
		return 'error';
	};

	const handleAddRow = () => {
		const newMappings = structuredClone(mappings);
		newMappings.push({
			targetField: null,
			sourceColumn: null,
			confidence: null,
		});
		setMappings(newMappings);
	};

	const handleRemoveRow = (index) => {
		const newMappings = structuredClone(mappings);
		newMappings.splice(index, 1);
		setMappings(newMappings);
	};

	const handleSourceHeaderChange = (newValue, index) => {
		const newMappings = structuredClone(mappings);
		const changedMapping = newMappings[index];
		changedMapping.sourceHeader = newValue;

		clearOrResetConfidence(changedMapping);

		setMappings(newMappings);
	};

	const handleTargetFieldChange = (newValue, index) => {
		const newMappings = structuredClone(mappings);
		const changedMapping = newMappings[index];
		changedMapping.targetField = newValue;

		clearOrResetConfidence(changedMapping);

		setMappings(newMappings);
	};

	// clear or restore confidence depending on whether it matches original
	const clearOrResetConfidence = (mapping) => {
		const originalMapping = originalMappings[mapping.sourceHeader];

		if (
			!originalMapping ||
			originalMapping.target_field != mapping.targetField
		) {
			mapping.confidence = 0;
		} else {
			mapping.confidence = originalMapping.confidence;
		}

		return mapping;
	};

	if (loading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '400px',
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography color='error'>{error}</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 4 }}>
			<Box>
				<Typography variant='h5' sx={{ mb: 2 }}>
					Mapping Review: {fileName}
				</Typography>
				<Typography variant='body2' color='textSecondary'>
					Review and confirm the field mappings between your source file and our
					data model
				</Typography>
			</Box>

			{/* Mapping Table */}
			<Paper>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Source Header</TableCell>
								<TableCell>Target Field</TableCell>
								<TableCell sx={{ width: 100 }}>Confidence</TableCell>
								<TableCell sx={{ width: 100 }}></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{mappings.map((mapping, index) => (
								<TableRow key={index}>
									<TableCell>
										<Autocomplete
											blurOnSelect
											value={mapping.sourceHeader}
											onChange={(_e, val) =>
												handleSourceHeaderChange(val, index)
											}
											options={sourceHeaders}
											getOptionDisabled={(option) =>
												!availableSourceHeaders.includes(option)
											}
											renderInput={(params) => (
												<TextField {...params} fullWidth />
											)}
										/>
									</TableCell>
									<TableCell>
										<Autocomplete
											blurOnSelect
											value={mapping.targetField}
											onChange={(_e, val) =>
												handleTargetFieldChange(val, index)
											}
											options={allTargetFields}
											getOptionDisabled={(option) =>
												!availableTargetFields.includes(option)
											}
											renderInput={(params) => (
												<TextField {...params} fullWidth />
											)}
										/>
									</TableCell>
									<TableCell align='center'>
										{!mapping.confidence ||
										!mapping.sourceHeader ||
										!mapping.targetField ? (
											<Chip size='small' label='N/A' color='default' />
										) : (
											<Chip
												size='small'
												icon={<AutoFixHighIcon />}
												label={`${Math.round(mapping.confidence * 100)}%`}
												color={getConfidenceColor(mapping.confidence)}
											/>
										)}
									</TableCell>
									<TableCell align='center'>
										<IconButton onClick={() => handleRemoveRow(index)}>
											<DeleteIcon />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			<Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
				<Button
					variant='contained'
					onClick={handleAddRow}
					startIcon={<AddIcon />}
				>
					Map Additional Fields
				</Button>
			</Box>

			{/* Data Preview */}
			<Box
				sx={{
					height: 400,
					display: 'flex',
					flexDirection: 'column',
					rowGap: 2,
				}}
			>
				<Typography variant='h6'>Data Preview</Typography>
				{previewRows.length > 0 ? (
					<DataGrid
						rows={previewRows}
						columns={previewColumns}
						initialState={{
							pagination: {
								paginationModel: { pageSize: 10 },
							},
						}}
						rowsPerPageOptions={[5]}
						disableSelectionOnClick
					/>
				) : (
					<Typography color='textSecondary'>
						No preview data available
					</Typography>
				)}
			</Box>

			{/* Action Buttons */}
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
				<Button variant='outlined' onClick={onClose}>
					Cancel
				</Button>
				<Button
					variant='outlined'
					startIcon={<SaveIcon />}
					onClick={handleSaveMapping}
					disabled={approved}
				>
					Save Without Approving
				</Button>
				<Button
					variant='contained'
					color='primary'
					startIcon={<CheckCircleOutlineIcon />}
					onClick={handleApproveMapping}
					disabled={approved}
				>
					Save and Approve
				</Button>
			</Box>
		</Box>
	);
};

export default MappingReview;
